import { useTagList } from '../hooks/tags/useTagList.ts';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifySuccess } from '../utils/NotifySuccess.tsx';
import useRemoveTag from '../hooks/tags/useRemoveTag.ts';
import { notifyError } from '../utils/NotifyError.tsx';
import { TagWithDescription } from '../models/tag.ts';
import { useDisclosure } from '@mantine/hooks';
import { AddTagModal } from '../components/modal/places/AddTagModal.tsx';
import { UpdateTagMetadataModal } from '../components/modal/places/UpdateTagMetadataModal.tsx';

const ITEM_PER_PAGE = 10;

const fuseOptions = {
  keys: ['name'],
  threshold: 0.4,
  distance: 100,
  ignoreLocation: true,
};

const TagListPage: React.FC = () => {
  const { data: tags, isLoading, error } = useTagList();
  const { mutateAsync: removeTag } = useRemoveTag();

  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [editTagId, setEditTagId] = useState<string | null>(null);

  const [
    addTagModalOpened,
    { open: openAddTagModal, close: closeAddTagModal },
  ] = useDisclosure(false);
  const [
    updateTagModalOpened,
    { open: openUpdateTagModal, close: closeUpdateTagModal },
  ] = useDisclosure(false);

  const filteredTags = useMemo(() => {
    if (!tags?.length) return [];

    const fuse = new Fuse(tags, fuseOptions);

    return searchValue
      ? fuse.search(searchValue).map((result) => result.item)
      : tags;
  }, [tags, searchValue]);

  const totalItems = filteredTags.length;

  const startIndex = (currentPage - 1) * ITEM_PER_PAGE;
  const paginatedTags = useMemo(() => {
    return filteredTags.slice(startIndex, startIndex + ITEM_PER_PAGE);
  }, [filteredTags, startIndex]);

  const columns: DataTableColumn<TagWithDescription>[] = [
    {
      title: '標籤名稱',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'name',
      width: '30%',
      render: ({ name }: { name: string }) => <Text size={'sm'}>{name}</Text>,
    },
    {
      title: '說明',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'description',
      width: '50%',
      ellipsis: true,
      cellsStyle: () => ({
        maxWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }),
      render: ({ description }: { description: string }) => (
        <Tooltip
          multiline
          position="top-start"
          label={
            <Text style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
              {description}
            </Text>
          }
          disabled={description === ''}
          styles={{
            tooltip: {
              maxWidth: 300,
            },
          }}
        >
          <Text
            size={'sm'}
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {description
              ? description.split('\n').length > 1
                ? `${description.split('\n')[0]} ...`
                : description
              : ''}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '操作',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'actions',
      width: '20%',
      render: ({ id }: { id: string }) => (
        <Group gap={8}>
          <Button
            variant={'outline'}
            size={'xs'}
            onClick={() => {
              setEditTagId(id);
              openUpdateTagModal();
            }}
          >
            編輯說明
          </Button>
          <Button
            color={'red'}
            variant={'outline'}
            size={'xs'}
            onClick={() => {
              modals.openConfirmModal({
                title: (
                  <Text style={{ fontSize: 16, fontWeight: 700 }}>
                    刪除標籤
                  </Text>
                ),
                children: (
                  <Text style={{ fontSize: 14, fontWeight: 400 }}>
                    是否確認刪除標籤？
                  </Text>
                ),
                centered: true,
                labels: { confirm: '確認', cancel: '取消' },
                confirmProps: { color: 'blue' },

                onConfirm: async () => {
                  await handleRemoveTagClick(id);
                },
              });
            }}
          >
            刪除
          </Button>
        </Group>
      ),
    },
  ];

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.currentTarget.value);
      setCurrentPage(1);
    },
    [],
  );

  const handleTagCreated = useCallback(async () => {
    closeAddTagModal();
    notifySuccess('新增標籤成功');
    setCurrentPage(1);
    await queryClient.invalidateQueries({
      queryKey: ['tags'],
      exact: true,
    });
  }, [queryClient, closeAddTagModal]);

  const handleAddTagClick = useCallback(() => {
    openAddTagModal();
  }, [openAddTagModal]);

  const handleRemoveTagClick = async (id: string) => {
    try {
      await removeTag(id);
      notifySuccess('刪除標籤成功');

      await queryClient.invalidateQueries({
        queryKey: ['tags'],
        exact: true,
      });
    } catch (error) {
      notifyError('刪除標籤失敗', error.message);
    }
  };

  const handleTagMetadataUpdated = useCallback(async () => {
    closeUpdateTagModal();
    notifySuccess('更新標籤說明成功');
    await queryClient.invalidateQueries({
      queryKey: ['tags'],
      exact: true,
    });
  }, [queryClient, closeUpdateTagModal]);

  if (isLoading) {
    return (
      <>
        <LoadingOverlay
          visible={true}
          overlayProps={{ radius: 'sm', blur: 1 }}
          loaderProps={{
            color: 'rgb(67,154,255)',
            type: 'bars',
            size: 'sm',
          }}
        />
      </>
    );
  }
  if (error) {
    return <div style={{ marginLeft: 'xl' }}>載入標籤時發生錯誤</div>;
  }

  return (
    <>
      {addTagModalOpened && (
        <AddTagModal
          opened={addTagModalOpened}
          onClose={closeAddTagModal}
          onSuccess={handleTagCreated}
        />
      )}
      {editTagId && updateTagModalOpened && (
        <UpdateTagMetadataModal
          opened={updateTagModalOpened}
          onClose={closeUpdateTagModal}
          tagId={editTagId}
          onSuccess={handleTagMetadataUpdated}
        />
      )}

      <Paper shadow="xl" radius="lg">
        <Group
          justify="space-between"
          p="24px 24px 20px 24px"
          style={{ borderBottom: 'none', flexShrink: 0 }}
        >
          <Title order={2}>標籤管理</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            size={'md'}
            variant="filled"
            onClick={handleAddTagClick}
          >
            新增標籤
          </Button>
        </Group>

        <div style={{ flexShrink: 0, paddingInline: 24, paddingBottom: 10 }}>
          <TextInput
            placeholder="搜尋標籤名稱"
            leftSection={<IconSearch size={16} />}
            value={searchValue}
            onChange={handleSearchChange}
            style={{ width: 321, height: 40 }}
          />
        </div>

        <Box style={{ paddingInline: 24, paddingBottom: 24 }}>
          <DataTable
            idAccessor={'id'}
            highlightOnHover
            minHeight={600}
            columns={columns}
            records={paginatedTags}
            recordsPerPage={ITEM_PER_PAGE}
            page={currentPage}
            onPageChange={setCurrentPage}
            fetching={isLoading}
            totalRecords={totalItems}
            verticalSpacing={'xs'}
            noRecordsText={'尚無標籤'}
            paginationText={({ from, to, totalRecords }) =>
              `顯示 ${from} - ${to} 筆標籤，共 ${totalRecords} 筆`
            }
            styles={{
              pagination: {
                color: 'gray',
              },
            }}
          />
        </Box>
      </Paper>
    </>
  );
};

export default TagListPage;
